import type { QueryClient } from "@tanstack/react-query";
import type { ChatWithLastAttendance, DocID } from "@/types";
import { scheduleCoalescedInvalidation } from "@/features/chat/realtime/coalescedInvalidation";

/** Janela da lista de Encerradas (fixa no servidor) e a do total do menu. */
export const CLOSED_LIST_DAYS = 90;
export const CLOSED_NAV_COUNT_DAYS = 7;

type ClosedRow = Pick<ChatWithLastAttendance, "lastAttendance">;

/**
 * A conversa encerrada foi finalizada por uma pessoa (`close_origin = human`)?
 * Nunca pelo `attendantId`. Qualquer outra origem (IA, follow-ups,
 * inatividade) é lida como "pela IA". A lista não filtra mais pela origem: os
 * chips "Pela IA" · "Por atendente" saíram porque hoje só pessoas finalizam
 * (SA-1439); a rota `/closed` segue aceitando `closedBy`.
 */
export const isClosedByHuman = (chat: ClosedRow): boolean =>
  chat.lastAttendance?.closeOrigin === "human";

/** Quem está logado, para dizer "você" na linha da encerrada. */
export type ClosedByViewer = {
  userId: DocID | null | undefined;
};

/**
 * Quem finalizou, do ponto de vista de quem está logado: `"me"`, o nome,
 * `null` (pessoa sem nome) ou `"ai"` (origem não humana: IA, follow-ups,
 * inatividade). "Você" é decidido pelo id de quem finalizou (`closedById`),
 * nunca pelo nome (homônimos) nem pelo `attendantId` (o Admin que esvazia a
 * fila finaliza sem ser creditado, SA-1368 F2-17).
 */
export type ClosedByActor =
  | { kind: "ai" }
  | { kind: "me" }
  | { kind: "person"; name: string | null };

export const resolveClosedByActor = (
  chat: ClosedRow,
  viewer: ClosedByViewer
): ClosedByActor => {
  if (!isClosedByHuman(chat)) return { kind: "ai" };
  const closedById = chat.lastAttendance?.closedById;
  if (viewer.userId && closedById && closedById === viewer.userId) {
    return { kind: "me" };
  }
  return {
    kind: "person",
    name: chat.lastAttendance?.closedByName?.trim() || null,
  };
};

/**
 * Linha "Encerrada por …" da lista (protótipo `closedByLabel`): "Encerrada por
 * você", "Encerrada por {Nome}" ou "Encerrada pela IA". Sem nome, "Encerrada
 * por atendente".
 */
export const closedByLabel = (
  chat: ClosedRow,
  viewer: ClosedByViewer
): string => {
  const actor = resolveClosedByActor(chat, viewer);
  if (actor.kind === "ai") return "Encerrada pela IA";
  if (actor.kind === "me") return "Encerrada por você";
  return `Encerrada por ${actor.name ?? "atendente"}`;
};

/** Data do encerramento (a ordem da lista de Encerradas). */
export const closedAtOf = (chat: ClosedRow): number => {
  const endDate = chat.lastAttendance?.end_date;
  return endDate ? new Date(endDate as unknown as string).getTime() || 0 : 0;
};

/**
 * Key da lista de Encerradas, no prefixo `["chats"]` (toda invalidação das
 * listas a refaz). Leva `attendantFilters: ["closed"]` para os patches de
 * socket (`updateCacheFor`, `removeChatFromLists`) a tratarem como as outras
 * listas: a conversa que vira `closed` entra, a que é reaberta sai. Com tag,
 * o socket trata como as outras listas filtradas por tag. `list: "closed"`
 * separa esta key da de `useChats` com o status `closed` (rota principal, outra
 * ordem e outra janela): sem ele as duas dividiriam o mesmo cache.
 */
export const closedChatsQueryKey = (
  sectorId: DocID | null | undefined,
  tagIds: readonly DocID[] = []
) =>
  [
    "chats",
    {
      sectorId: sectorId ?? null,
      attendantFilters: ["closed"],
      list: "closed",
      search: "",
      tagIds: [...tagIds].sort(),
      scope: "all",
      areaIds: [],
    },
  ] as const;

/**
 * Key do total de Encerradas dos últimos 7 dias (menu). Sem `attendantFilters`
 * de propósito, como a contagem de abertas: os patches de socket não mexem
 * nela, quem a mantém certa é `invalidateClosedChatsCount` quando uma conversa
 * entra ou sai de `closed`.
 */
export const closedChatsCountQueryKey = (sectorId: DocID | null | undefined) =>
  ["chats", { sectorId: sectorId ?? null, closedChatsCount: true }] as const;

/** Agrupada como a contagem de abertas (SA-1452). */
export const invalidateClosedChatsCount = (
  queryClient: QueryClient,
  sectorId: DocID | null | undefined
): void =>
  scheduleCoalescedInvalidation(
    queryClient,
    closedChatsCountQueryKey(sectorId),
    { exact: true }
  );

/** Data do encerramento em dd/mm/aaaa (faixa da conversa encerrada). */
export const formatClosedDate = (value: unknown): string | null => {
  if (!value) return null;
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Faixa da conversa encerrada aberta (protótipo `closedBanner`): `“motivo” ·
 * por {Nome} em dd/mm/aaaa`; sem motivo, só `por {Nome} em ...`; origem não
 * humana, `pela IA em ...`; finalizada por mim, `por você em ...`.
 */
export const closedBannerText = (
  chat: ClosedRow,
  viewer: ClosedByViewer
): string => {
  const actor = resolveClosedByActor(chat, viewer);
  const by =
    actor.kind === "ai"
      ? "pela IA"
      : actor.kind === "me"
        ? "por você"
        : `por ${actor.name ?? "atendente"}`;
  const day = formatClosedDate(chat.lastAttendance?.end_date);
  const reason = chat.lastAttendance?.closeReason?.trim();
  return `${reason ? `“${reason}” · ` : ""}${by}${day ? ` em ${day}` : ""}`;
};
